import React from 'react';

import { arrivalNoticeHooks, dashboardArrivalNoticeHooks } from '@apis/next/arrivalNoticeApis';
import { dashboardPdfApis, pdfApis } from '@apis/next/pdfApis';
import { dashboardPreAlertApis, preAlertApis } from '@apis/next/preAlertApis';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { arrivalNoticeStrings } from '@data/arrivalNotice';
import { useSearch } from '@hooks/next/useSearch';
import { useBlobURL } from '@hooks/useBlobURL';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { printBlob } from '@utils/printBlob';
import { stringifySearch, toJST } from '@utils/types';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    print: '印刷',
    changeLog: '変更履歴',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  exportArrivalNoticeContainer: {
    ...common.flexColumn,
    ...common.itemsCenter,
  },
  footer: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.middleText,
    marginTop: '2rem',
  },
  operationPanel: {
    ...common.flexCenter,
    ...common.middleText,
    position: 'fixed',
    bottom: 0,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',

    width: '100%',
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
  },
  operationBox: {
    ...common.flexInline,
    ...common.middleText,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',
    maxWidth: 750,
    width: '100%',
  },
  info: {
    ...common.flexColumn,
    flex: 1,
    rowGap: '0.5rem',
  },
  operation: {
    ...common.flexCenter,
    columnGap: 15,
  },
}));

export const ExportArrivalNotice: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(
    z.object({
      d2dNo: z.string(),
      isClient: z.boolean(),
    }),
  );

  const preAlertDetails = search.isClient
    ? preAlertApis.useGet('/pre-alert/:d2d_no', {
        params: { d2d_no: search.d2dNo },
      })
    : dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
        params: { d2d_no: search.d2dNo },
      });

  const arrivalNotice = search.isClient
    ? arrivalNoticeHooks.useGet(
        '/arrival-notice/:id',
        {
          params: { id: preAlertDetails.data?.arrival_notice_id! },
        },
        {
          enabled: !!preAlertDetails.data?.arrival_notice_id,
        },
      )
    : dashboardArrivalNoticeHooks.useGet(
        '/dashboard/arrival-notice/:id',
        {
          params: { id: preAlertDetails.data?.arrival_notice_id! },
        },
        {
          enabled: !!preAlertDetails.data?.arrival_notice_id,
        },
      );

  const pdfQuery = search.isClient
    ? pdfApis.useGet('/pdf/ArrivalNotice/:d2d_no', {
        params: {
          d2d_no: search.d2dNo,
        },
      })
    : dashboardPdfApis.useGet('/dashboard/pdf/ArrivalNotice/:d2d_no', {
        params: {
          d2d_no: search.d2dNo,
        },
      });

  const pdfUrl = useBlobURL(pdfQuery.data);

  const renderOperationPanel = () => {
    return (
      <div className={classes.operationPanel}>
        <div className={classes.operationBox}>
          <div className={classes.info}>
            <div>Latest Update: {toJST(new Date(arrivalNotice.data?.updated_at!))}</div>
            <div>
              <a
                href={`${url('/utilities/Bookings/PrintArrivalNoticeLogs')}?${stringifySearch({
                  id: preAlertDetails.data?.arrival_notice_id!,
                  isClient: search.isClient,
                })}`}
                target="_blank"
                className={classes.link}
              >
                {strings.changeLog}
              </a>
            </div>
          </div>

          <div className={classes.operation}>
            <Button
              color="primary"
              text={strings.print}
              type="button"
              shape="round"
              size="small"
              onClick={() => {
                printBlob(pdfUrl!);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout
      hideHeader
      hideFooter
      hideCookiePolicy
      title={`${arrivalNoticeStrings.arrivalNotice} - ${search.d2dNo}`}
      loading={preAlertDetails.isLoading || arrivalNotice.isLoading || pdfQuery.isLoading}
      errors={[preAlertDetails.error, arrivalNotice.error, pdfQuery.error]}
      classes={{
        background: classes.background,
      }}
    >
      {() => (
        <div className={classes.exportArrivalNoticeContainer}>
          <Document file={pdfUrl} loading="">
            <Page pageNumber={1} width={900} />
          </Document>

          {renderOperationPanel()}
        </div>
      )}
    </Layout>
  );
};
